import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Calendar, CheckCircle2, Loader2, Link2, Unlink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function GoogleCalendarCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/google/status"); setStatus(data); }
    catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const g = params.get("google");
    if (g === "connected") toast.success("Google Calendar conectado");
    if (g === "error") toast.error("No se pudo conectar Google Calendar. Inténtalo de nuevo.");
    if (g) window.history.replaceState({}, "", window.location.pathname);
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get("/google/connect"); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/google/disconnect"); toast.success("Google Calendar desconectado"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!status) return null;

  const needsReconnect = status.needs_reconnect;

  return (
    <div className="mb-5 bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="google-calendar-card">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${needsReconnect ? "bg-amber-100" : status.connected ? "bg-[#7FD498]/25" : "bg-slate-100"}`}>
          <Calendar className={`h-5 w-5 ${needsReconnect ? "text-amber-600" : status.connected ? "text-[#2f7a45]" : "text-slate-400"}`} />
        </div>
        <div>
          <p className="font-bold text-[#373E45]">Google Calendar</p>
          {!status.configured ? (
            <p className="text-xs text-[#64748B]">Pendiente de configurar las credenciales en el servidor.</p>
          ) : needsReconnect ? (
            <p className="text-xs text-amber-700 font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />La conexión caducó. Reconéctalo para seguir creando eventos en tu calendario.</p>
          ) : status.connected ? (
            <p className="text-xs text-[#2f7a45] font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Conectado · las citas confirmadas se crean en tu calendario</p>
          ) : (
            <p className="text-xs text-[#64748B]">Conéctalo para que las citas confirmadas se añadan automáticamente a tu calendario.</p>
          )}
        </div>
      </div>
      {status.configured && (
        needsReconnect ? (
          <Button size="sm" onClick={connect} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shrink-0" data-testid="google-reconnect-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" />Reconectar Google Calendar</>}
          </Button>
        ) : status.connected ? (
          <Button size="sm" variant="outline" onClick={disconnect} disabled={busy} className="rounded-xl border-slate-300 text-[#373E45] font-bold shrink-0" data-testid="google-disconnect-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Unlink className="h-4 w-4 mr-1.5" />Desconectar</>}
          </Button>
        ) : (
          <Button size="sm" onClick={connect} disabled={busy} className="bg-[#373E45] hover:bg-[#2b3137] text-white font-bold rounded-xl shrink-0" data-testid="google-connect-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" />Conectar Google Calendar</>}
          </Button>
        )
      )}
    </div>
  );
}
